import React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grid2 as Grid,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { CloudSync as CloudSyncIcon } from '@mui/icons-material';
import { useUnifiedEasyflexProfile } from '../../hooks/useUnifiedEasyflexProfile';

/**
 * Read-only replacement for the old EasyFlexWorkerForm.
 *
 * Central-db is now the sole source of truth for EasyFlex profile fields
 * (personal details, addresses, licenses, sync state). This panel is a
 * read-only surface backed by `useUnifiedEasyflexProfile` — no local mirror,
 * no editable form, no pull/save/delete buttons. Recruiter edits happen in
 * EasyFlex itself; central-db picks them up on the next sync.
 */
interface EasyflexProfilePanelProps {
    easyflexRegistrationNumber?: string | null;
    /** Disambiguates regnum collisions across the two EasyFlex orgs. */
    easyflexOrganization?: string | null;
}

function formatAddress(addr: unknown): string {
    if (!addr || typeof addr !== 'object') return '—';
    const a = addr as Record<string, unknown>;
    const line1 = [a.street, a.houseNumber, a.addition].filter(Boolean).join(' ');
    const line2 = [a.postalCode, a.city].filter(Boolean).join(' ');
    const line3 = a.country as string | undefined;
    const parts = [line1, line2, line3].filter((p) => p && String(p).trim() !== '');
    return parts.length > 0 ? parts.join(', ') : '—';
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value ?? '—'}
            </Typography>
        </Stack>
    );
}

const EasyflexProfilePanel: React.FC<EasyflexProfilePanelProps> = ({ easyflexRegistrationNumber, easyflexOrganization }) => {
    const theme = useTheme();
    const { data, loading, error } = useUnifiedEasyflexProfile(easyflexRegistrationNumber, easyflexOrganization);

    if (!easyflexRegistrationNumber) {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    This worker isn't linked to EasyFlex.
                </Typography>
            </Paper>
        );
    }

    if (loading) {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading EasyFlex profile…
                </Typography>
            </Paper>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Failed to load EasyFlex profile: {(error as Error)?.message ?? 'unknown error'}
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert severity="warning">
                No EasyFlex profile found for regnum {easyflexRegistrationNumber}.
            </Alert>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <CloudSyncIcon sx={{ color: theme.palette.info.main }} />
                <Typography variant="h6" fontWeight={700}>
                    EasyFlex profile
                </Typography>
                <Chip
                    size="small"
                    label={`Regnum ${data.easyflexRegistrationNumber}`}
                    variant="outlined"
                />
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    Central-db is the source of truth. Edits happen in EasyFlex.
                </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Personal
                    </Typography>
                    <InfoRow label="First name" value={data.firstName} />
                    <InfoRow label="Last name" value={data.lastName} />
                    <InfoRow label="Date of birth" value={data.dateOfBirth} />
                    <InfoRow label="Gender" value={data.gender} />
                    <InfoRow label="Nationality" value={data.nationality} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Contact
                    </Typography>
                    <InfoRow label="Email" value={data.email} />
                    <InfoRow label="Phone" value={data.phone} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Postal address
                    </Typography>
                    <Typography variant="body2">{formatAddress(data.postalAddress)}</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Residential address
                    </Typography>
                    <Typography variant="body2">{formatAddress(data.residentialAddress)}</Typography>
                </Grid>

                {data.driverLicense && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Driver's licence
                        </Typography>
                        <InfoRow label="Number" value={data.driverLicense.number} />
                        <InfoRow label="Valid until" value={data.driverLicense.validUntil} />
                    </Grid>
                )}

                {data.workPermit && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Work permit
                        </Typography>
                        <InfoRow label="Type" value={data.workPermit.type} />
                        <InfoRow label="Number" value={data.workPermit.number} />
                        <InfoRow label="Valid until" value={data.workPermit.validUntil} />
                    </Grid>
                )}
            </Grid>
        </Paper>
    );
};

export default EasyflexProfilePanel;
